import { useCallback } from 'react';

type HapticPattern = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';

// Pattern configurations for different feedback types
// Values in milliseconds for vibration duration
const patterns: Record<HapticPattern, number[]> = {
  light: [8],           // Ultra-light tap - iOS-style selection
  medium: [15],         // Standard tap
  heavy: [25],          // Noticeable impact
  selection: [5],       // Very subtle selection change
  success: [10, 30, 10], // Double pulse for success
  warning: [20, 20, 20], // Triple pulse warning
  error: [40, 30, 40],   // Stronger error signal
};

// Check if vibration API is available (once at load time)
const canVibrate = typeof navigator !== 'undefined' && 'vibrate' in navigator;

export function useHaptic() {
  const vibrate = useCallback((pattern: HapticPattern = 'light') => {
    if (!canVibrate) return;
    
    try {
      navigator.vibrate(patterns[pattern]);
    } catch (e) {
      // Silently fail - vibration may not be allowed
    }
  }, []);

  // Pre-bound convenience methods for common haptic patterns
  const success = useCallback(() => vibrate('success'), [vibrate]);
  const warning = useCallback(() => vibrate('warning'), [vibrate]);
  const error = useCallback(() => vibrate('error'), [vibrate]);
  const tap = useCallback(() => vibrate('light'), [vibrate]);
  const impact = useCallback(() => vibrate('medium'), [vibrate]);
  const selection = useCallback(() => vibrate('selection'), [vibrate]);
  const heavy = useCallback(() => vibrate('heavy'), [vibrate]);

  return { 
    vibrate, 
    success, 
    warning, 
    error, 
    tap, 
    impact, 
    selection, 
    heavy,
    isSupported: canVibrate 
  };
}

// Standalone function for use outside React components
export function triggerHaptic(pattern: HapticPattern = 'light') {
  if (!canVibrate) return;
  
  try {
    navigator.vibrate(patterns[pattern]);
  } catch (e) {
    // Silently fail
  }
}
